import { memo } from 'react';
import { Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Move } from '@draughts/shared';
import { formatMoveNotation } from '@draughts/core';
import { useGameStore } from '../../store/game-store';
import { colorLeading } from '../../utils/challenge-icons';
import { moverOf } from '../../hooks/useBlindMoveAnnouncement';
import './BlindMoveAnnouncement.css';

interface BlindMoveAnnouncementProps {
  /** La mossa da annunciare, o `null` per non mostrare niente. */
  move: Move | null;
}

/**
 * «33-28» nella riga di stato della card, al posto di «Tocca a te».
 *
 * Sta LÌ e non sopra la damiera per due ragioni: sopra la damiera copriva le caselle (e
 * prima ancora se ne mangiava i tocchi), e la riga di stato ha già lo spazio riservato —
 * il posto del turno resta montato anche da vuoto — quindi la schermata non si allunga,
 * che a 360px è la cosa da difendere.
 *
 * Puramente presentazionale: quando compare e quando sparisce lo decide
 * `useBlindMoveAnnouncement`.
 */
export const BlindMoveAnnouncement = memo(function BlindMoveAnnouncement({ move }: BlindMoveAnnouncementProps) {
  const { t } = useTranslation();
  const variant = useGameStore((s) => s.variant);
  // Serve perché si annunciano anche le proprie mosse: due annunci di fila senza un segno
  // di chi ha giocato sono ambigui a colpo d'occhio. Il conto (il turno è già ruotato) sta
  // in `moverOf`, condiviso col blocco dell'ultima mossa in barra.
  const currentTurn = useGameStore((s) => s.currentTurn);
  if (!move) return null;
  const mover = moverOf(currentTurn);

  return (
    /* `role="status"`: è una regione viva, cioè il modo in cui l'annuncio arriva a chi non
       guarda lo schermo — che nel gioco bendato è precisamente il caso d'uso. */
    <span className="blind-move-note" role="status" data-testid="blind-move-announcement">
      {/* L'etichetta esce dalla vista (nello slot non c'è larghezza per etichetta PIÙ
          notazione) ma non dal significato: senza, uno screen reader legge «33-28» e
          basta, che da solo non vuol dire niente. */}
      <span className="blind-move-note__label">{t('game.blind.lastMove')}: </span>
      <span className="blind-move-note__dot" aria-hidden="true">{colorLeading(mover, 12)}</span>
      {formatMoveNotation(move, variant)}
      {/* Bendati, sapere che l'avversario ha appena fatto dama è l'informazione che pesa
          di più: la notazione da sola non la dice. */}
      {move.isPromotion && <Crown size={12} aria-label={t('game.blind.promoted')} />}
    </span>
  );
});

export default BlindMoveAnnouncement;
